import { Navigate, useLocation } from "react-router-dom";
import { useUserContext } from "@/contexts/UserContext";

type UserRole = "venue_owner" | "coach" | "player" | "admin";

interface Props {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
}

const ROLE_HOME: Record<UserRole, string> = {
  venue_owner: "/venue/dashboard",
  coach:       "/coach/dashboard",
  player:      "/app/home",
  admin:       "/admin/dashboard",
};

function ProtectedRoute({ children, allowedRoles = [] }: Props) {
  const { user, profile, loading } = useUserContext();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Verificando acceso...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Si está autenticado pero sin perfil → completar perfil
  if (!profile) {
    if (location.pathname === "/complete-profile") return <>{children}</>;
    return <Navigate to="/complete-profile" state={{ from: location }} replace />;
  }

  const role = profile.role as UserRole;

  if (allowedRoles.length > 0 && !allowedRoles.includes(role)) {
    return <Navigate to={ROLE_HOME[role] ?? "/app/home"} replace />;
  }

  return <>{children}</>;
}

export const VenueProtectedRoute = ({ children }: { children: React.ReactNode }) => (
  <ProtectedRoute allowedRoles={["venue_owner"]}>{children}</ProtectedRoute>
);

export const CoachProtectedRoute = ({ children }: { children: React.ReactNode }) => (
  <ProtectedRoute allowedRoles={["coach", "venue_owner"]}>{children}</ProtectedRoute>
);

export const AppProtectedRoute = ({ children }: { children: React.ReactNode }) => (
  <ProtectedRoute allowedRoles={["player", "coach", "venue_owner"]}>{children}</ProtectedRoute>
);

export const AdminProtectedRoute = ({ children }: { children: React.ReactNode }) => (
  <ProtectedRoute allowedRoles={["admin"]}>{children}</ProtectedRoute>
);

export default ProtectedRoute;
